import {
  CircularProgress,
  makeStyles,
  Popover,
  Tooltip,
} from '@material-ui/core';
import { DescriptionOutlined } from '@material-ui/icons';
import EditIcon from '@material-ui/icons/Edit';
import { ADDITIONAL_BILLING_STATUS } from 'doc-mate-store/lib/constants/load';
import { useStores } from 'doc-mate-store/lib/hooks';
import React, { useCallback, useEffect, useState } from 'react';
import { downloadAdditionalBillingPdf } from '../../../utils/paperworkPdf';

type Props = {
  cell: {
    value: [
      string, // text
      number | string, // id
      boolean, // isDispatcher
      string, // status

      boolean?, // isBilling
      string?, // as400 manifest number
    ];
  };
};

const LinkAdditional: React.FC<Props> = props => {
  const classes = useStyles();
  const { rootStore } = useStores();
  const { cell } = props;
  const {
    value: [
      text,
      id,
      isDispatcher,
      status,
      isBilling = false,
      as400ManifestNumber,
    ],
  } = cell;

  const [copy, setCopy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  useEffect(() => {
    if (copy) {
      setTimeout(() => setCopy(false), 1000);
    }
  }, [copy]);

  const handleEdit = useCallback(
    (event: React.MouseEvent<HTMLSpanElement>) => {
      event.stopPropagation();
      rootStore.setUpdateAdditionalBillingId(Number(id));
    },
    [rootStore, id],
  );

  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      navigator.clipboard.writeText(text);
      setAnchorEl(event.currentTarget);
      setCopy(true);
    },
    [text],
  );

  const handleDownloadPaperwork = useCallback(
    async (event: React.MouseEvent<HTMLSpanElement>) => {
      event.preventDefault();
      event.stopPropagation();

      if (!id || downloading) return;

      setDownloading(true);
      try {
        const legIdNumber = Number(id);
        const legIdString = String(id);

        // Clear in-memory model so newly uploaded files are fetched fresh
        rootStore.additionalBillingLegs.delete(legIdString);

        await rootStore.fetchAdditionalBillingLeg(legIdNumber);
        const leg = rootStore.additionalBillingLegs.get(legIdString);

        if (!leg) {
          alert('Unable to load document details for this additional billing.');
          return;
        }
        console.log('JUSS', as400ManifestNumber);
        const result = await downloadAdditionalBillingPdf(
          leg,
          as400ManifestNumber,
        );
        if (result.empty) {
          alert('No paperwork or documents available to export yet.');
        }
      } catch (err) {
        console.error('Failed to download additional billing paperwork:', err);
        alert('An error occurred while generating the paperwork PDF.');
      } finally {
        setDownloading(false);
      }
    },
    [id, downloading, rootStore, as400ManifestNumber],
  );

  return (
    <>
      <div className={classes.root}>
        <span className={classes.link} onClick={handleClick}>
          {text}
        </span>
        <span className={classes.iconContainer}>
          {/* Billing Paperwork Download */}
          {isBilling && (
            <span
              className={classes.icon}
              onClick={handleDownloadPaperwork}
              role="button"
              tabIndex={0}
            >
              <Tooltip title="Download Paperwork">
                {downloading ? (
                  <CircularProgress size={14} color="primary" />
                ) : (
                  <DescriptionOutlined
                    aria-label="Download Paperwork"
                    color="primary"
                    fontSize="inherit"
                  />
                )}
              </Tooltip>
            </span>
          )}

          {/* Dispatcher Actions */}
          {isDispatcher && status !== ADDITIONAL_BILLING_STATUS.INVOICED && (
            <span className={classes.icon} onClick={handleEdit}>
              <Tooltip title="Edit Additional Billing">
                <EditIcon
                  aria-label="Edit Item"
                  color="primary"
                  fontSize="inherit"
                />
              </Tooltip>
            </span>
          )}
        </span>
      </div>
      <Popover
        id={text}
        open={copy}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        <div className={classes.popUpText}>Copied</div>
      </Popover>
    </>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    flexDirection: 'row',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
  },
  link: {
    overflow: 'hidden',
    paddingRight: theme.spacing(0.25),
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
  },
  iconContainer: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flex: 1,
  },
  icon: {
    display: 'inline-flex',
    alignItems: 'center',
    paddingRight: theme.spacing(1),
    cursor: 'pointer',
  },
  popUpText: {
    fontSize: 12,
    padding: 5,
  },
}));

export default React.memo(LinkAdditional);
